import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router-dom';
import { ICandidateFilter, ISubmissionAnalyticsPayload } from 'wisestep-api';
import { getDateUsingFormat, WsColors } from 'wisestep-base';

import getEncodedQueryParams from '../../../../../utils/getEncodedQueryParams';
import { Widget } from '../../../DerivedCharts/helper';
import {
  getDateRangeByTimePeriod,
  getDateRangeByTimePeriodForTTS,
  getLocalISODateTime,
} from '../../../utils/timeInfo';
import { overrideAnalyticsFilter, renderCellFor } from '../../helper';
import { overrideQueryFilter } from '../../jobs/utils/jobsHelper';
import { IPostgreSubmissionDTO } from '../types';

export const renderSubmissionCountCell = (
  params,
  filterNameValuePairs: [string, string][],
  analyticsFilter: any[],
  query: ISubmissionAnalyticsPayload
) => {
  const overridenFilters = overrideAnalyticsFilter(analyticsFilter, filterNameValuePairs);
  return (
    <RouterLink
      to={`/all-submissions?search=${encodeSubmissionFilters(overridenFilters, query)}`}
      target="_blank"
      style={{
        textDecoration: 'none',
        color: WsColors.TheBlues700,
      }}
    >
      {params.value}
    </RouterLink>
  );
};

export const getColumns = (
  widgetName: Widget,
  widgetData: IPostgreSubmissionDTO,
  tableData: any[],
  query: ISubmissionAnalyticsPayload,
  analyticsFilter: ICandidateFilter[]
): GridColDef[] => {
  const extraColumns = widgetData.response?.map((entity) => ({
    field: entity.date?.split('T')[query.frequency === 'HOURLY' ? 1 : 0] as string,
    headerName:
      entity.date?.split('T')[query.frequency === 'HOURLY' ? 1 : 0] + ` (${entity.count})`,
    width: 120,
  }));
  const uniqueStages = Array.from(
    new Set(widgetData?.resultSet?.flatMap((record) => Object.keys(record?.stageWithCount ?? {})))
  );
  switch (widgetName) {
    case Widget.TABLE_CLIENTWISE:
      return [
        {
          field: 'clientName',
          headerName: 'Client Name',
          minWidth: 120,
          flex: 2,
          renderCell: (params) => renderCellFor('CLIENT', params),
        },
        {
          field: 'clientContact',
          headerName: 'Client Contact',
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('CLIENT_RECRUITER', params),
        },
        {
          field: 'totalCount',
          headerName: `Total (${tableData.reduce(
            (accumulator, currentRow) => currentRow.totalCount + accumulator,
            0
          )})`,
          minWidth: 120,
          flex: 1,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [
                ['CLIENT_NAMES', params.row.clientName],
                ['CLIENT_RECRUITERS', params.row.clientContactEmail],
              ],
              analyticsFilter,
              query
            ),
        },
        ...(extraColumns as any[]),
      ];
    case Widget.TABLE_BY_AM:
      return [
        {
          field: 'accountManager',
          headerName: 'Account Manager',
          minWidth: 160,
          flex: 3,
          renderCell: (params) => renderCellFor('ACCOUNT_MANAGER', params),
        },
        { field: 'jobType', headerName: 'Job Type', minWidth: 100, flex: 1 },
        {
          field: 'totalCount',
          headerName: `Total (${tableData.reduce(
            (accumulator, currentRow) => currentRow.totalCount + accumulator,
            0
          )})`,
          minWidth: 120,
          flex: 1,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [
                ['ACCOUNT_MANAGERS', params.row.accountManagerEmail],
                ['JOB_TYPE', params.row.jobType],
              ],
              analyticsFilter,
              query
            ),
        },
        ...(extraColumns as any[]),
      ];
    case Widget.TABLE_JOB_TITLE:
      return [
        {
          field: 'jobTitle',
          headerName: 'Job Title',
          minWidth: 200,
          flex: 2,
          renderCell: (params) => renderCellFor('JOB', params),
        },
        {
          field: 'clientName',
          headerName: 'Client Name',
          minWidth: 120,
          flex: 1,
          renderCell: (params) => renderCellFor('CLIENT', params),
        },
        {
          field: 'jobCode',
          headerName: 'Job Code',
          width: 100,
          renderCell: (params) => renderCellFor('JOB', params),
        },
        {
          field: 'jobType',
          headerName: `Job Type`,
          minWidth: 100,
          flex: 1,
        },
        {
          field: 'count',
          headerName: `Count (${widgetData.count})`,
          minWidth: 110,
          flex: 1,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [['JOB_ID', params.row.jobId]],
              analyticsFilter,
              query
            ),
        },
      ];
    case Widget.TABLE_RECRUITERWISE:
      return [
        {
          field: 'recruiterName',
          headerName: 'User Name',
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('RECRUITER', params),
        },
        {
          field: 'teamName',
          headerName: 'Team Name',
          minWidth: 120,
          flex: 1,
          renderCell: (params) => renderCellFor('TEAM', params),
        },
        {
          field: 'jobType',
          headerName: 'Job Type',
          minWidth: 100,
          flex: 1,
        },
        {
          field: 'count',
          headerName: `Count (${widgetData.count})`,
          width: 120,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [
                ['TEAM_IDS', params.row.teamId],
                ['RECRUITER_NAMES', params.row.recruiterEmail],
                ['JOB_TYPE', params.row.jobType],
              ],
              analyticsFilter,
              query
            ),
        },
      ];
    case Widget.TABLE_USERS_WITH_TEAMS:
      return [
        {
          field: 'recruiterName',
          headerName: 'Recruiter Name',
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('RECRUITER', params),
        },
        {
          field: 'teamName',
          headerName: 'Team Name',
          minWidth: 120,
          flex: 1,
          renderCell: (params) => renderCellFor('TEAM', params),
        },
        {
          field: 'totalCount',
          headerName: `Total (${tableData.reduce(
            (accumulator, currentRow) => currentRow.totalCount + accumulator,
            0
          )})`,
          width: 120,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [
                ['TEAM_IDS', params.row.teamId],
                ['RECRUITER_NAMES', params.row.recruiterEmail],
              ],
              analyticsFilter,
              query
            ),
        },
        ...(extraColumns ?? []),
      ];
    case Widget.TABLE_BY_CLIENT_SPOC:
      return [
        {
          field: 'recruiterName',
          headerName: `Client SPOC Name`,
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('CLIENT_RECRUITER', params),
        },
        {
          field: 'count',
          headerName: `Total (${widgetData.count})`,
          width: 120,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [['CLIENT_RECRUITERS', params.row.clientContactEmail]],
              analyticsFilter,
              query
            ),
        },
      ];
    case Widget.TABLE_BY_DOWNLOAD_SOURCE:
      return [
        {
          field: 'downloadSource',
          headerName: 'Download Source Name',
          minWidth: 120,
          flex: 2,
        },
        {
          field: 'count',
          headerName: `Total (${widgetData.count})`,
          minWidth: 120,
          flex: 1,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [['DOWNLOAD_SOURCE', params.row.downloadSource]],
              analyticsFilter,
              query
            ),
        },
      ];
    case Widget.TABLE_SPLIT_BY_STAGES:
      return [
        {
          field: 'recruiterName',
          headerName: 'Recruiter Name',
          minWidth: 160,
          flex: 1,
          renderCell: (params) => renderCellFor('RECRUITER', params),
        },
        {
          field: 'teamName',
          headerName: 'Team Name',
          minWidth: 120,
          flex: 1,
          renderCell: (params) => renderCellFor('TEAM', params),
        },
        {
          field: 'jobAccountManagerName',
          headerName: 'Account Manager',
          minWidth: 160,
          flex: 1,
          renderCell: (params) => renderCellFor('ACCOUNT_MANAGER', params),
        },
        {
          field: 'companyName',
          headerName: 'Client',
          minWidth: 120,
          flex: 1,
          renderCell: (params) => renderCellFor('CLIENT', params),
        },
        {
          field: 'jobClientRecruiterName',
          headerName: 'Client SPOC',
          minWidth: 160,
          flex: 1,
          renderCell: (params) => renderCellFor('CLIENT_RECRUITER', params),
        },
        {
          field: 'jobCode',
          headerName: 'Job Code',
          width: 100,
          renderCell: (params) => renderCellFor('JOB', params),
        },
        {
          field: 'mappedJobTitle',
          headerName: 'Job Title',
          minWidth: 200,
          flex: 1,
          renderCell: (params) => renderCellFor('JOB', params),
        },
        {
          field: 'count',
          headerName: 'Total',
          minWidth: 120,
          flex: 1,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [
                ['CLIENT_RECRUITERS', params.row.clientContactEmail],
                ['TEAM_IDS', params.row.teamId],
                ['RECRUITER_NAMES', params.row.recruiterEmail],
                ['CLIENT_NAMES', params.row.clientName],
                ['ACCOUNT_MANAGERS', params.row.accountManagerEmail],
              ],
              analyticsFilter,
              query
            ),
        },
      ];
    case Widget.TABLE_TO_AM:
      return [
        {
          field: 'accountManager',
          headerName: `Account Manager Name`,
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('ACCOUNT_MANAGER', params),
        },
        {
          field: 'count',
          headerName: `Total (${widgetData.count ?? 0})`,
          minWidth: 120,
          flex: 1,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [['ACCOUNT_MANAGERS', params.row.accountManagerEmail]],
              analyticsFilter,
              query
            ),
        },
      ];
    case Widget.TABLE_TO_CLIENTS:
      return [
        {
          field: 'companyName',
          headerName: `Client Name`,
          minWidth: 120,
          flex: 2,
          renderCell: (params) => renderCellFor('CLIENT', params),
        },
        {
          field: 'count',
          headerName: `Total (${widgetData.count})`,
          minWidth: 120,
          flex: 1,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [['CLIENT_NAMES', params.row.companyName]],
              analyticsFilter,
              query
            ),
        },
      ];
    case Widget.TABLE_BY_JOBTYPE:
      return [
        {
          field: 'jobType',
          headerName: `Job Type Name`,
          minWidth: 100,
          flex: 1,
        },
        {
          field: 'count',
          headerName: `Total (${widgetData.count ?? 0})`,
          minWidth: 120,
          flex: 1,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [['JOB_TYPE', params.row.jobType]],
              analyticsFilter,
              query
            ),
        },
      ];
    case Widget.TABLE_BY_SOURCE:
      return [
        {
          field: 'source',
          headerName: 'Source Name',
          minWidth: 120,
          flex: 2,
        },
        {
          field: 'count',
          headerName: `Total (${widgetData.count})`,
          minWidth: 120,
          flex: 1,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [['SOURCE', params.row.source]],
              analyticsFilter,
              query
            ),
        },
      ];
    case Widget.TABLE_BY_USERS:
      return [
        {
          field: 'recruiterName',
          headerName: `User Name`,
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('RECRUITER', params),
        },
        {
          field: 'count',
          headerName: `Total (${widgetData.count})`,
          minWidth: 120,
          flex: 1,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [['RECRUITER_NAMES', params.row.recruiterEmail]],
              analyticsFilter,
              query
            ),
        },
      ];
    case Widget.TABLE_STAGE_STATE:
      return [
        {
          field: 'stage',
          headerName: 'Stage',
          minWidth: 120,
          flex: 1.5,
        },
        {
          field: 'state',
          headerName: 'State',
          minWidth: 130,
          flex: 1.5,
        },
        {
          field: 'count',
          headerName: `Total (${widgetData.count})`,
          minWidth: 130,
          flex: 1,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [
                [
                  'STAGE_STATUS',
                  JSON.stringify({ stage: params.row.stage, selectedStatuses: [params.row.state] }),
                ],
              ],
              analyticsFilter,
              query
            ),
        },
      ];
    case Widget.TABLE_JOB_SUBMISSION_COUNT:
      return [
        {
          field: 'count',
          headerName: `Submissions`,
          flex: 1.5,
        },
        {
          field: 'jobCount',
          headerName: `Jobs`,
          flex: 1,
        },
      ];
    case Widget.TABLE_CANDIDATE_WISE_STAGE_DETAILS:
    case Widget.TABLE_CANDIDATE_STAGE_DETAILS_WJD:
    case Widget.TABLE_JOINING_DATE:
      return [
        {
          field: 'candidateName',
          headerName: 'Candidate Name',
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('CANDIDATE', params),
        },
        {
          field: 'recruiterName',
          headerName: 'User Name',
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('RECRUITER', params),
        },
        {
          field: 'mappedJobTitle',
          headerName: 'Job Title',
          minWidth: 200,
          flex: 1.5,
          renderCell: (params) => renderCellFor('JOB', params),
        },
        {
          field: 'jobCode',
          headerName: 'Job Code',
          width: 100,
          renderCell: (params) => renderCellFor('JOB', params),
        },
        {
          field: 'companyName',
          headerName: 'Client Name',
          minWidth: 120,
          flex: 1.5,
          renderCell: (params) => renderCellFor('CLIENT', params),
        },
        {
          field: 'jobClientRecruiterName',
          headerName: 'Client SPOC',
          minWidth: 160,
          flex: 1.5,
          renderCell: (params) => renderCellFor('CLIENT_RECRUITER', params),
        },
        {
          field: 'jobAccountManagerName',
          headerName: 'Account Manager',
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('ACCOUNT_MANAGER', params),
        },
        {
          field: 'applicationStage',
          headerName: 'Stage',
          minWidth: 110,
          flex: 1.5,
        },
        {
          field: 'applicationState',
          headerName: 'State',
          minWidth: 130,
          flex: 1.5,
        },
        {
          field: 'modifiedOn',
          headerName: 'Updated Date',
          width: 130,
          renderCell: (params) =>
            params.value ? getDateUsingFormat(params.value, 'YYYY-MM-DD') : '',
        },
        {
          field: 'joiningDate',
          headerName: 'Joining Date',
          width: 130,
          renderCell: (params) =>
            params.value ? getDateUsingFormat(params.value, 'YYYY-MM-DD') : '',
        },
      ];
    case Widget.TABLE_RECRUITER_WISE_STAGES:
      return [
        {
          field: 'recruiterName',
          headerName: 'Recruiter Name',
          width: 200,
          renderCell: (params) => renderCellFor('RECRUITER_ID', params),
        },
        {
          field: 'count',
          headerName: `Total (${widgetData?.count ?? 0})`,
          width: 200,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [['RECRUITER_NAMES', params.row.recruiterId]],
              analyticsFilter,
              query
            ),
        },
        ...uniqueStages.map((stage) => ({
          field: stage,
          headerName: stage,
          width: 170,
        })),
      ];
    case Widget.TABLE_TIME_TO_SERVICE:
      return [
        {
          field: 'time',
          headerName: 'Time',
          minWidth: 160,
          flex: 2,
        },
        {
          field: 'numberOfSubmissions',
          headerName: `No of Submissions (${tableData.reduce(
            (accumulator, currentRow) => currentRow.numberOfSubmissions + accumulator,
            0
          )})`,
          minWidth: 100,
          flex: 1,
          // renderCell: (params) =>
          //   renderSubmissionCountCell(
          //     params,
          //     [['DATE_RANGE_TTS', params.row.time]],
          //     analyticsFilter,
          //     query
          //   ),
        },
      ];
    case Widget.TABLE_CANDIDATE_WISE_TIME_TO_SERVICE:
      return [
        {
          field: 'candidateName',
          headerName: 'Candidate Name',
          minWidth: 160,
          flex: 1,
          renderCell: (params) => renderCellFor('CANDIDATE', params),
        },
        {
          field: 'createdBy',
          headerName: 'Submitted User',
          minWidth: 100,
          flex: 1,
          renderCell: (params) => renderCellFor('RECRUITER_ID', params),
        },
        {
          field: 'timeTakenForInitialSubmission',
          headerName: 'Time Taken for Initial Submission',
          minWidth: 100,
          flex: 1,
        },
        {
          field: 'currentStageState',
          headerName: 'Current Stage - State',
          minWidth: 150,
          flex: 1,
        },
        {
          field: 'timeToCurrentStage',
          headerName: 'TAT from initial submission',
          minWidth: 100,
          flex: 1,
        },
      ];
    case Widget.TABLE_SUBMISSION_ASSIGN_TIME_TO_SERVICE:
      return [
        {
          field: 'assignedUser',
          headerName: 'Assigned User',
          minWidth: 150,
          flex: 1.5,
          renderCell: (params) => renderCellFor('RECRUITER_ID', params),
        },
        {
          field: 'candidates',
          headerName: 'Candidates',
          minWidth: 100,
          flex: 1,
        },
        {
          field: 'minTimeToProcess',
          headerName: 'Min Time to process the queue',
          minWidth: 100,
          flex: 1,
        },
        {
          field: 'medianTimeToProcess',
          headerName: 'Median Time to process the queue',
          minWidth: 150,
          flex: 1,
        },
        {
          field: 'maxTimeToProcess',
          headerName: 'Max Time to process the queue',
          minWidth: 100,
          flex: 1,
        },
      ];
    default:
      throw new Error('Invalid table widget name');
  }
};

export const getDynamicColumns = (
  widgetName: Widget,
  columnData: Array<{
    label: string;
    accessor: string;
    flex?: number;
    renderCell?: (params: GridRenderCellParams<any, any, any>) => React.ReactNode;
  }>
): Array<GridColDef> => {
  switch (widgetName) {
    case Widget.TABLE_CANDIDATE_WISE_TIME_TO_SERVICE_MEDIAN:
      return columnData.map<GridColDef>(({ label, accessor, flex, renderCell }) => ({
        field: accessor,
        headerName: label,
        minWidth: 100,
        flex,
        renderCell,
      }));
    default:
      return columnData.map<GridColDef>(({ label, accessor, flex, renderCell }) => ({
        field: accessor,
        headerName: label,
        minWidth: 100,
        flex,
        renderCell,
      }));
  }
};

export function getTableData(
  widgetName: Widget,
  widgetData: IPostgreSubmissionDTO,
  frequency: string
) {
  const fieldsByTable = {
    [Widget.TABLE_CLIENTWISE]: [
      'jobCode',
      'clientName',
      'clientContactId',
      'clientContact',
      'clientContactEmail',
    ],
    [Widget.TABLE_BY_AM]: ['accountManager', 'jobType', 'accountManagerEmail'],
    [Widget.TABLE_USERS_WITH_TEAMS]: ['recruiterName', 'teamName', 'recruiterEmail', 'teamId'],
  };
  const uniqueStages = Array.from(
    new Set(widgetData?.resultSet?.flatMap((record) => Object.keys(record?.stageWithCount ?? {})))
  );
  switch (widgetName) {
    case Widget.TABLE_BY_AM:
    case Widget.TABLE_CLIENTWISE:
    case Widget.TABLE_USERS_WITH_TEAMS:
      return transformTableData(widgetData, frequency as string, fieldsByTable[widgetName]);
    case Widget.TABLE_BY_USERS:
    case Widget.TABLE_TO_AM:
    case Widget.TABLE_TO_CLIENTS:
    case Widget.TABLE_BY_SOURCE:
    case Widget.TABLE_BY_DOWNLOAD_SOURCE:
    case Widget.TABLE_BY_CLIENT_SPOC:
    case Widget.TABLE_BY_JOBTYPE:
    case Widget.TABLE_JOB_TITLE:
    case Widget.TABLE_STAGE_STATE:
    case Widget.TABLE_RECRUITERWISE:
    case Widget.TABLE_JOB_SUBMISSION_COUNT:
    case Widget.TABLE_TIME_TO_SERVICE:
    case Widget.TABLE_CANDIDATE_WISE_TIME_TO_SERVICE:
    case Widget.TABLE_SUBMISSION_ASSIGN_TIME_TO_SERVICE:
      widgetData.resultSet?.forEach((result, index) => (result.id = index));
      return widgetData.resultSet;
    case Widget.TABLE_SPLIT_BY_STAGES:
    case Widget.TABLE_CANDIDATE_WISE_STAGE_DETAILS:
    case Widget.TABLE_CANDIDATE_STAGE_DETAILS_WJD:
    case Widget.TABLE_JOINING_DATE:
      widgetData.data?.forEach((result, index) => (result.id = index));
      return widgetData.data;
    case Widget.TABLE_RECRUITER_WISE_STAGES:
      widgetData?.resultSet?.forEach((record, index) => {
        record.id = index;
        uniqueStages.forEach((stage) => {
          record[stage] = record?.stageWithCount?.[stage] || 0;
        });
      });
      return widgetData?.resultSet;
    case Widget.TABLE_CANDIDATE_WISE_TIME_TO_SERVICE_MEDIAN:
      widgetData?.resultSet?.forEach((record, index) => {
        record.id = index;
        record.stageHistory.forEach(({ stage, timeToReach }) => {
          const durationMs = timeToReach || 0;
          const totalMinutes = Math.floor(durationMs / (1000 * 60));
          const days = Math.floor(totalMinutes / (60 * 24));
          const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
          const minutes = totalMinutes % 60;
          let formatted = '';
          if (days > 0) {
            formatted = `${days} d ${hours} h`;
          } else if (hours > 0) {
            formatted = `${hours} h ${minutes} min`;
          } else {
            formatted = `${minutes} min`;
          }
          record[stage] = formatted;
        });
      });
      return widgetData?.resultSet;
    default:
      return [];
  }
}

export const transformTableData = (
  input: IPostgreSubmissionDTO,
  frequency: string,
  uniqueKeyFields: string[]
) => {
  const output: { id: string; totalCount: number; [key: string]: number | string }[] = [];
  const allDates = new Set<string>();
  input.response?.forEach((item) =>
    allDates.add(item.date?.split('T')[frequency === 'HOURLY' ? 1 : 0] as string)
  );
  input.response?.forEach((item) => {
    const dateKey = item.date?.split('T')[frequency === 'HOURLY' ? 1 : 0] as string;
    item.results?.forEach((result) => {
      const uniqueKey = uniqueKeyFields.map((field) => result[field]).join('_');
      const existingEntry = output.find((entry) => entry.id === uniqueKey);

      if (existingEntry) {
        existingEntry.totalCount += result.count;
        existingEntry[dateKey] = ((existingEntry[dateKey] as number) ?? 0) + result.count;
      } else {
        const newEntry = {
          id: uniqueKey,
          totalCount: result.count,
        };
        uniqueKeyFields.forEach((field) => {
          newEntry[field] = result[field];
        });
        allDates.forEach((date) => (newEntry[date] = 0));
        newEntry[dateKey] = result.count;
        output.push(newEntry);
      }
    });
  });

  return output;
};

export const mapAnalyticsToSubmissionFilter = (
  analyticsGlobalFilters: ICandidateFilter[],
  query: ISubmissionAnalyticsPayload
): ICandidateFilter[] => {
  let isDateFilterAdded = false;
  const hasDateRangeTTS = (analyticsGlobalFilters ?? []).some(
    (f) => f.filterName === 'DATE_RANGE_TTS'
  );
  const submissionFilters: ICandidateFilter[] = overrideQueryFilter(
    query.filters ?? [],
    analyticsGlobalFilters
  )
    .filter(
      (analyticsfilter) =>
        analyticsfilter.filterName != 'SMART_FILTERS' &&
        analyticsfilter.filterName != 'BUSINESS_DEFINITION' &&
        !(hasDateRangeTTS && analyticsfilter.filterName === 'DATE_RANGE')
    )
    .map((filter) => {
      switch (filter.filterName) {
        case 'DATE_RANGE': {
          isDateFilterAdded = true;
          const values = JSON.parse(filter.values[0] as string);
          let filterDateBy = '';
          if (values.filterBy.length == 0) {
            filterDateBy =
              query.filterDate == 'modified_on'
                ? 'UPDATED_BETWEEN'
                : query.filterDate == 'joining_date'
                ? 'JOINING_BETWEEN'
                : 'CREATED_BETWEEN';
          } else {
            filterDateBy = values.filterBy;
          }
          return {
            filterName:
              query.filterDate == 'joining_date'
                ? 'JOINING_BETWEEN'
                : 'SUBMITTED_OR_UPDATED_BETWEEN',
            values: [
              filterDateBy,
              getLocalISODateTime(new Date(values.fromDate)),
              getLocalISODateTime(new Date(values.toDate)),
            ],
          };
        }
        case 'DATE_RANGE_TTS': {
          isDateFilterAdded = true;
          const values = filter.values[0] as string;
          const { fromDateTime, toDateTime } = getDateRangeByTimePeriodForTTS(values, query);
          return {
            filterName: 'SUBMITTED_OR_UPDATED_BETWEEN',
            values: ['CREATED_BETWEEN', fromDateTime, toDateTime],
          };
        }
        case 'ACCOUNT_MANAGERS':
          return {
            filterName: 'ACCOUNT_MANAGER',
            values: filter.values,
          };
        case 'RECRUITER_NAMES':
          return {
            filterName: 'SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB',
            values: filter.values,
          };
        case 'TEAM_IDS':
          return {
            filterName: 'SUBMITTED_TEAM_ASSIGNED_JOB',
            values: filter.values,
          };
        case 'JOB_STATUS': {
          if (filter.values.length == 0) return filter;
          const values = (filter.values[0] as string).toLowerCase();
          return {
            filterName: filter.filterName,
            values: values == 'opened' ? [false] : values == 'closed' ? [true] : [true, false],
          };
        }
        case 'STAGE_STATUS': {
          const stageStatusObject = Object.fromEntries(
            (filter.values as string[]).map((filterValue) => {
              const stageStatus = JSON.parse(filterValue);
              return [stageStatus.stage, stageStatus.selectedStatuses];
            })
          );
          return {
            filterName: 'STAGE_STATUS',
            fields: stageStatusObject,
            values: [],
          };
        }
        case 'JOB_IDS':
          return {
            filterName: 'JOB_ID',
            values: filter.values,
          };
        case 'BUSINESS_UNIT_ID':
          return {
            filterName: 'BUSINESS_UNIT',
            values: filter.values,
          };
        default:
          return filter;
      }
    });

  if (!isDateFilterAdded && !hasDateRangeTTS) {
    submissionFilters.push({
      filterName:
        query.filterDate == 'joining_date' ? 'JOINING_BETWEEN' : 'SUBMITTED_OR_UPDATED_BETWEEN',
      values: [
        query.filterDate == 'modified_on'
          ? 'UPDATED_BETWEEN'
          : query.filterDate == 'joining_date'
          ? 'JOINING_BETWEEN'
          : 'CREATED_BETWEEN',
        ...Object.values(getDateRangeByTimePeriod(query.range as string)),
      ],
    });
  }
  return submissionFilters;
};

export function encodeSubmissionFilters(filter, query) {
  return getEncodedQueryParams({
    businessDefinitionId: query.businessDefinitionId,
    query: '',
    page: 0,
    size: 15,
    sort: [],
    workspaceId: '',
    filters: mapAnalyticsToSubmissionFilter(filter, query),
  });
}
